import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ProjectInquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: '',
    attachment: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const projectTypeOptions = [
    { value: 'web-app', label: 'Web Application' },
    { value: 'mobile-app', label: 'Mobile Application' },
    { value: 'e-commerce', label: 'E-commerce Platform' },
    { value: 'api-backend', label: 'API & Backend Development' },
    { value: 'consulting', label: 'Technical Consulting' },
    { value: 'maintenance', label: 'Maintenance & Support' },
    { value: 'other', label: 'Other' }
  ];

  const budgetOptions = [
    { value: '5k-10k', label: '$5,000 - $10,000' },
    { value: '10k-25k', label: '$10,000 - $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k+', label: '$100,000+' },
    { value: 'discuss', label: 'Let\'s discuss' }
  ];

  const timelineOptions = [
    { value: 'asap', label: 'ASAP' },
    { value: '1-month', label: '1 Month' },
    { value: '2-3-months', label: '2-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: '6-months+', label: '6+ Months' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.projectType) {
      newErrors.projectType = 'Please select a project type';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData?.message?.trim()?.length < 20) {
      newErrors.message = 'Message must be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file?.size > 10 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          attachment: 'File size must be less than 10MB'
        }));
        return;
      }

      // Check file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/jpeg',
        'image/png'
      ];

      if (!allowedTypes?.includes(file?.type)) {
        setErrors(prev => ({
          ...prev,
          attachment: 'Please upload PDF, DOC, DOCX, TXT, JPG, or PNG files only'
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        attachment: file
      }));

      // Clear attachment error
      if (errors?.attachment) {
        setErrors(prev => ({
          ...prev,
          attachment: ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = new FormData();
      
      // REPLACE THIS STRING WITH YOUR ACTUAL ACCESS KEY
      submissionData.append("access_key", "3b262011-94a5-4bf2-b29f-0760220178aa");
      
      submissionData.append("subject", `New Project Inquiry from ${formData.name}`);

      submissionData.append("name", formData.name);
      submissionData.append("email", formData.email);
      submissionData.append("company", formData.company || "Not provided");
      submissionData.append("projectType", formData.projectType);
      submissionData.append("budget", formData.budget || "Not provided");
      submissionData.append("timeline", formData.timeline || "Not provided");
      submissionData.append("message", formData.message);

      if (formData.attachment) {
        submissionData.append("attachment", formData.attachment);
      }

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: submissionData
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          budget: '',
          timeline: '',
          message: '',
          attachment: null
        });

        // Reset the file input in the DOM
        const fileInput = document.getElementById('file-upload');
        if (fileInput) fileInput.value = '';

      } else {
        throw new Error(data.message || "Failed to submit form");
      }
      
    } catch (error) {
      console.error("Submission Error:", error);
      setErrors({
        submit: 'Failed to send message. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-surface rounded-xl p-8 shadow-subtle text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Message Sent Successfully!
        </h3>
        <p className="text-muted-foreground mb-6">
          Thank you for reaching out. I'll get back to you within 24 hours.
        </p>
        <Button
          variant="outline"
          onClick={() => setIsSubmitted(false)}
        >
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-xl p-6 shadow-subtle">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Let's Work Together
        </h2>
        <p className="text-muted-foreground">
          Tell me about your project and I'll get back to you with a detailed proposal.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            placeholder="John Doe"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />
          <Input
            label="Email Address"
            type="email"
            placeholder="john@company.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
        </div>

        <Input
          label="Company/Organization"
          type="text"
          placeholder="Your Company Name (Optional)"
          value={formData?.company}
          onChange={(e) => handleInputChange('company', e?.target?.value)}
        />

        {/* Project Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Project Type"
            placeholder="Select project type"
            options={projectTypeOptions}
            value={formData?.projectType}
            onChange={(value) => handleInputChange('projectType', value)}
            error={errors?.projectType}
            required
          />
          <Select
            label="Budget Range"
            placeholder="Select budget"
            options={budgetOptions}
            value={formData?.budget}
            onChange={(value) => handleInputChange('budget', value)}
          />
          <Select
            label="Timeline"
            placeholder="Select timeline"
            options={timelineOptions}
            value={formData?.timeline}
            onChange={(value) => handleInputChange('timeline', value)}
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Project Description <span className="text-error">*</span>
          </label>
          <textarea
            rows={6}
            placeholder="Tell me about your project, goals, and any specific requirements..."
            value={formData?.message}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
            className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 ${
              errors?.message 
                ? 'border-error focus:border-error focus:ring-error/20' :'border-border'
            }`}
          />
          {errors?.message && (
            <p className="mt-1 text-sm text-error">{errors?.message}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {formData?.message?.length}/500 characters (minimum 20)
          </p>
        </div>

        {/* File Attachment */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Attachment (Optional)
          </label>
          <div className="relative">
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="flex items-center justify-center w-full p-4 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors duration-200"
            >
              <div className="text-center">
                <Icon name="Upload" size={24} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-foreground">
                  {formData?.attachment ? formData?.attachment?.name : 'Click to upload file'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, DOC, DOCX, TXT, JPG, PNG (Max 10MB)
                </p>
              </div>
            </label>
          </div>
          {errors?.attachment && (
            <p className="mt-1 text-sm text-error">{errors?.attachment}</p>
          )}
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          {errors?.submit && (
            <p className="text-sm text-error mb-4">{errors?.submit}</p>
          )}
          <Button
            type="submit"
            variant="default"
            loading={isSubmitting}
            disabled={isSubmitting}
            fullWidth
            iconName="Send"
            iconPosition="right"
          >
            {isSubmitting ? 'Sending Message...' : 'Send Message'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProjectInquiryForm;
